import { useEffect, useState } from 'react';
import { slideRegistry } from '../../../core/plugin-system/SlideRegistry';
import { initializePluginSystem, registerAllPlugins } from '../../../config/slide-plugins.config';
import type { AnswerDTO, LessonDTO } from '../../../core/types/lesson.types';

interface AnswerSlidesListProps {
    lesson: LessonDTO | null;
    answers: AnswerDTO[] | undefined;
    loading: boolean;
}

export function AnswerSlidesList({
    lesson,
    answers,
    loading,
}: AnswerSlidesListProps) {
    // Track plugin initialization state
    const [pluginsReady, setPluginsReady] = useState(slideRegistry.isInitialized());

    // Initialize plugin system if not already done (e.g., when navigating directly to report page)
    useEffect(() => {
        const initPlugins = async () => {
            if (!slideRegistry.isInitialized()) {
                try {
                    initializePluginSystem();
                    await registerAllPlugins();
                    setPluginsReady(true);
                } catch (error) {
                    console.error('Failed to initialize plugins:', error);
                }
            }
        };
        initPlugins();
    }, []);

    // Show loading spinner until data arrives AND plugins are ready
    // Must check both loading flag AND answers existence to prevent race condition
    if (loading || !answers || !pluginsReady) {
        return (
            <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground mt-2">Se încarcă răspunsurile...</p>
            </div>
        );
    }

    // Get only slides that have answers with actual content
    // Filter out empty answers (no text, no photos, no selections)
    const answeredSlides = lesson?.slides?.filter(slide => {
        const answer = answers.find(a => a.slideId === slide.id);
        if (!answer) return false;

        // Skip slides with no content
        const hasText = !!answer.text?.trim();
        const hasPhotos = (answer.photos?.length || 0) > 0;
        const hasSelections = (answer.selectedOptionsIds?.length || 0) > 0;

        return hasText || hasPhotos || hasSelections;
    }) || [];

    if (answeredSlides.length === 0) {
        return (
            <p className="text-muted-foreground text-center py-8">
                Nu există răspunsuri de afișat
            </p>
        );
    }

    return (
        <div className="space-y-6">
            <h3 className="font-semibold text-lg">Răspunsuri</h3>

            {answeredSlides.map((slide, index) => {
                const plugin = slideRegistry.get(slide.type!);
                const slideAnswer = answers?.find(a => a.slideId === slide.id);

                if (!plugin || !slideAnswer) return null;

                return (
                    <div
                        key={slide.id}
                        className="border border-border rounded-lg p-4 bg-card"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <span className="font-semibold text-foreground">
                                Slide {index + 1}:
                            </span>
                            <span className="text-sm text-muted-foreground">
                                {plugin.displayName}
                            </span>
                        </div>

                        {/* Use plugin's report component */}
                        <plugin.ReportComponent
                            slide={slide}
                            answer={slideAnswer}
                        />
                    </div>
                );
            })}
        </div>
    );
}
